import type { Aabb, ArenaTeam } from "@townsy/contracts";
import { baseballWallDistance, type ArenaField, type BaseballField, type BasketballField, type FootballField, type HockeyField, type SoccerField } from "@townsy/world";

/**
 * Canvas painters for the Sports Park stadiums (ADR-0044): each playing surface with its markings, drawn from the
 * shared geometry in @townsy/world so the lines are where the simulation plays (the yard lines, the penalty area, the
 * diamond, the three-point arc, the blue lines). Original artwork, generated from numbers.
 *
 * A surface canvas is a top-down map of a rectangle in world metres: north (+z) up, east (+x) right, `ppm` pixels per
 * metre (the 3D maps it onto a ground plane the same way).
 */
export type Ctx = CanvasRenderingContext2D;

export interface SurfaceMap {
  rect: Aabb;
  ppm: number;
  width: number;
  height: number;
}

export function surfaceMap(rect: Aabb, maxPixels = 2048, maxPpm = 48): SurfaceMap {
  const w = rect.maxX - rect.minX;
  const h = rect.maxZ - rect.minZ;
  const ppm = Math.min(maxPpm, maxPixels / Math.max(w, h));
  return { rect, ppm, width: Math.round(w * ppm), height: Math.round(h * ppm) };
}

const px = (m: SurfaceMap, x: number) => (x - m.rect.minX) * m.ppm;
const py = (m: SurfaceMap, z: number) => (m.rect.maxZ - z) * m.ppm;

function line(c: Ctx, m: SurfaceMap, x0: number, z0: number, x1: number, z1: number, lw: number): void {
  c.lineWidth = Math.max(1, lw * m.ppm);
  c.beginPath();
  c.moveTo(px(m, x0), py(m, z0));
  c.lineTo(px(m, x1), py(m, z1));
  c.stroke();
}

function rectLine(c: Ctx, m: SurfaceMap, x0: number, z0: number, x1: number, z1: number, lw: number): void {
  c.lineWidth = Math.max(1, lw * m.ppm);
  c.strokeRect(px(m, Math.min(x0, x1)), py(m, Math.max(z0, z1)), Math.abs(x1 - x0) * m.ppm, Math.abs(z1 - z0) * m.ppm);
}

function fillRect(c: Ctx, m: SurfaceMap, x0: number, z0: number, x1: number, z1: number, color: string): void {
  c.fillStyle = color;
  c.fillRect(px(m, Math.min(x0, x1)), py(m, Math.max(z0, z1)), Math.abs(x1 - x0) * m.ppm, Math.abs(z1 - z0) * m.ppm);
}

/** Circle or arc (world angles a0..a1 from +x toward +z) around (x, z). */
function arc(c: Ctx, m: SurfaceMap, x: number, z: number, r: number, lw: number, a0 = 0, a1 = Math.PI * 2, fill?: string): void {
  c.lineWidth = Math.max(1, lw * m.ppm);
  c.beginPath();
  // Canvas y grows southward: world angle a is canvas angle -a.
  c.arc(px(m, x), py(m, z), r * m.ppm, -a1, -a0);
  if (fill) {
    c.fillStyle = fill;
    c.fill();
  } else c.stroke();
}

function dot(c: Ctx, m: SurfaceMap, x: number, z: number, r: number, color: string): void {
  c.fillStyle = color;
  c.beginPath();
  c.arc(px(m, x), py(m, z), Math.max(1.5, r * m.ppm), 0, Math.PI * 2);
  c.fill();
}

function grain(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Mown grass: alternating stripes (along x) with a little speckle. */
function grass(c: Ctx, m: SurfaceMap, x0: number, z0: number, x1: number, z1: number, stripe: number, a: string, b: string, alongZ = false): void {
  const n = Math.ceil((alongZ ? z1 - z0 : x1 - x0) / stripe);
  for (let i = 0; i < n; i++) {
    const color = i % 2 === 0 ? a : b;
    if (alongZ) fillRect(c, m, x0, z0 + i * stripe, x1, Math.min(z1, z0 + (i + 1) * stripe), color);
    else fillRect(c, m, x0 + i * stripe, z0, Math.min(x1, x0 + (i + 1) * stripe), z1, color);
  }
  const rnd = grain(7);
  const count = Math.round(((x1 - x0) * (z1 - z0) * m.ppm * m.ppm) / 900);
  for (let i = 0; i < count; i++) {
    c.fillStyle = rnd() < 0.5 ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)";
    c.fillRect(px(m, x0 + rnd() * (x1 - x0)), py(m, z0 + rnd() * (z1 - z0)), 2, 2);
  }
}

/** Text centred at (x, z), `heightM` tall, shrunk to fit `maxWidthM` (if given). */
function text(c: Ctx, m: SurfaceMap, s: string, x: number, z: number, heightM: number, color: string, rotate = 0, font = "800", maxWidthM = 0): void {
  c.save();
  c.translate(px(m, x), py(m, z));
  c.rotate(rotate);
  c.fillStyle = color;
  let size = Math.max(8, heightM * m.ppm);
  c.font = `${font} ${size}px "Arial Black", Arial, sans-serif`;
  while (maxWidthM > 0 && size > 8 && c.measureText(s).width > maxWidthM * m.ppm) {
    size *= 0.92;
    c.font = `${font} ${size}px "Arial Black", Arial, sans-serif`;
  }
  c.textAlign = "center";
  c.textBaseline = "middle";
  c.fillText(s, 0, 0);
  c.restore();
}

export const ARENA_PAINT = {
  grassA: "#3F8F3A",
  grassB: "#37822F",
  surround: "#2F6E2A",
  line: "#F7F7F2",
  dirt: "#B9824F",
  dirtDark: "#A26F40",
  maple: "#D9A867",
  mapleAlt: "#CF9C5C",
  courtSurround: "#20263A",
  ice: "#EEF4F9",
  iceShade: "#E2EBF3",
  red: "#C8302B",
  blue: "#1F4BB8",
  track: "#3B4354",
} as const;

// ---------------------------------------------------------------------------------------------
// The five surfaces
// ---------------------------------------------------------------------------------------------

export function paintFootball(c: Ctx, m: SurfaceMap, f: FootballField, home: ArenaTeam, awayName: string): void {
  const r = m.rect;
  fillRect(c, m, r.minX, r.minZ, r.maxX, r.maxZ, ARENA_PAINT.surround);
  const hx = f.length / 2;
  const hz = f.width / 2;
  grass(c, m, f.cx - f.goalLineX, f.cz - hz, f.cx + f.goalLineX, f.cz + hz, 5 * f.yard, ARENA_PAINT.grassA, ARENA_PAINT.grassB);
  // End zones in the home team's colour (west) and dark (east), with the names.
  fillRect(c, m, f.cx - hx, f.cz - hz, f.cx - f.goalLineX, f.cz + hz, home.primary);
  fillRect(c, m, f.cx + f.goalLineX, f.cz - hz, f.cx + hx, f.cz + hz, "#2B3346");
  text(c, m, "TOWNSY", f.cx - (hx + f.goalLineX) / 2, f.cz, 5.2, home.secondary, -Math.PI / 2, "800", f.width - 6);
  text(c, m, awayName.toUpperCase(), f.cx + (hx + f.goalLineX) / 2, f.cz, 3.4, "#FFFFFF", Math.PI / 2, "800", f.width - 6);
  c.strokeStyle = ARENA_PAINT.line;
  rectLine(c, m, f.cx - hx, f.cz - hz, f.cx + hx, f.cz + hz, 0.15);
  // Yard lines every 5 yards, hash marks every yard, numbers every 10.
  for (let y = 0; y <= 100; y += 5) {
    const x = f.cx - f.goalLineX + y * f.yard;
    line(c, m, x, f.cz - hz, x, f.cz + hz, y % 50 === 0 ? 0.2 : 0.12);
  }
  for (let y = 1; y < 100; y++) {
    if (y % 5 === 0) continue;
    const x = f.cx - f.goalLineX + y * f.yard;
    for (const zc of [-hz + 0.5, -3.08, 3.08, hz - 0.5]) line(c, m, x, f.cz + zc - 0.3, x, f.cz + zc + 0.3, 0.08);
  }
  for (let y = 10; y <= 90; y += 10) {
    const n = String(y <= 50 ? y : 100 - y);
    const x = f.cx - f.goalLineX + y * f.yard;
    text(c, m, n, x, f.cz - hz + 4.2, 1.8, ARENA_PAINT.line, 0);
    text(c, m, n, x, f.cz + hz - 4.2, 1.8, ARENA_PAINT.line, Math.PI);
  }
  // The Townsy star at midfield.
  arc(c, m, f.cx, f.cz, 3.2, 0.2, 0, Math.PI * 2, home.primary);
  text(c, m, "T", f.cx, f.cz, 3.6, home.secondary, 0);
}

export function paintSoccer(c: Ctx, m: SurfaceMap, f: SoccerField): void {
  const r = m.rect;
  fillRect(c, m, r.minX, r.minZ, r.maxX, r.maxZ, ARENA_PAINT.surround);
  const hx = f.length / 2;
  const hz = f.width / 2;
  grass(c, m, f.cx - hx, f.cz - hz, f.cx + hx, f.cz + hz, f.length / 14, ARENA_PAINT.grassA, ARENA_PAINT.grassB);
  c.strokeStyle = ARENA_PAINT.line;
  const lw = 0.12;
  rectLine(c, m, f.cx - hx, f.cz - hz, f.cx + hx, f.cz + hz, lw);
  line(c, m, f.cx, f.cz - hz, f.cx, f.cz + hz, lw);
  arc(c, m, f.cx, f.cz, f.centreCircle, lw);
  dot(c, m, f.cx, f.cz, 0.22, ARENA_PAINT.line);
  for (const s of [-1, 1]) {
    const gx = f.cx + s * hx;
    const pa = f.penaltyArea;
    const ga = f.goalArea;
    rectLine(c, m, gx, f.cz - pa.width / 2, gx - s * pa.depth, f.cz + pa.width / 2, lw);
    rectLine(c, m, gx, f.cz - ga.width / 2, gx - s * ga.depth, f.cz + ga.width / 2, lw);
    const spot = gx - s * f.penaltySpot;
    dot(c, m, spot, f.cz, 0.2, ARENA_PAINT.line);
    // The penalty arc: the part of the 9.15 m circle around the spot outside the area.
    const d = pa.depth - f.penaltySpot;
    const a = Math.acos(Math.min(1, d / f.centreCircle));
    if (s > 0) arc(c, m, spot, f.cz, f.centreCircle, lw, Math.PI - a, Math.PI + a);
    else arc(c, m, spot, f.cz, f.centreCircle, lw, -a, a);
    // Corner arcs.
    for (const t of [-1, 1]) {
      const cz = f.cz + t * hz;
      const a0 = s > 0 ? (t > 0 ? Math.PI : Math.PI / 2) : t > 0 ? -Math.PI / 2 : 0;
      arc(c, m, gx, cz, 1, lw, a0, a0 + Math.PI / 2);
    }
  }
}

export function paintBaseball(c: Ctx, m: SurfaceMap, f: BaseballField): void {
  const r = m.rect;
  // Foul ground and the warning track first, then the fair outfield grass to the wall.
  fillRect(c, m, r.minX, r.minZ, r.maxX, r.maxZ, ARENA_PAINT.surround);
  const fan = (radius: (yaw: number) => number, color: string) => {
    c.fillStyle = color;
    c.beginPath();
    c.moveTo(px(m, f.home.x), py(m, f.home.z));
    for (let i = 0; i <= 60; i++) {
      const yaw = -Math.PI / 4 + (i / 60) * (Math.PI / 2);
      const d = radius(yaw);
      c.lineTo(px(m, f.home.x + Math.sin(yaw) * d), py(m, f.home.z + Math.cos(yaw) * d));
    }
    c.closePath();
    c.fill();
  };
  fan((y) => baseballWallDistance(f, y), ARENA_PAINT.dirtDark);
  // Mowed outfield stripes inside the warning track.
  c.save();
  c.beginPath();
  c.moveTo(px(m, f.home.x), py(m, f.home.z));
  for (let i = 0; i <= 60; i++) {
    const yaw = -Math.PI / 4 + (i / 60) * (Math.PI / 2);
    const d = baseballWallDistance(f, yaw) - 4.5;
    c.lineTo(px(m, f.home.x + Math.sin(yaw) * d), py(m, f.home.z + Math.cos(yaw) * d));
  }
  c.closePath();
  c.clip();
  grass(c, m, r.minX, r.minZ, r.maxX, r.maxZ, 6, ARENA_PAINT.grassA, ARENA_PAINT.grassB, true);
  c.restore();
  // The infield dirt: the arc around the mound and the base paths, then the infield grass square.
  arc(c, m, f.mound.x, f.mound.z, f.infieldRadius, 0.1, 0, Math.PI * 2, ARENA_PAINT.dirt);
  fillRect(c, m, f.home.x - 40, f.home.z - 12, f.home.x + 40, f.home.z, ARENA_PAINT.surround);
  c.save();
  c.fillStyle = ARENA_PAINT.grassA;
  const inset = 2.6;
  c.beginPath();
  const k = Math.SQRT1_2;
  c.moveTo(px(m, f.home.x), py(m, f.home.z + inset * 1.4));
  c.lineTo(px(m, f.first.x - inset * k * 1.4), py(m, f.first.z));
  c.lineTo(px(m, f.second.x), py(m, f.second.z - inset * 1.4));
  c.lineTo(px(m, f.third.x + inset * k * 1.4), py(m, f.third.z));
  c.closePath();
  c.fill();
  c.restore();
  // The home-plate circle and the mound.
  arc(c, m, f.home.x, f.home.z, 4, 0.1, 0, Math.PI * 2, ARENA_PAINT.dirt);
  arc(c, m, f.mound.x, f.mound.z, 2.74, 0.1, 0, Math.PI * 2, ARENA_PAINT.dirt);
  // Foul lines to the wall and the batter's boxes.
  c.strokeStyle = ARENA_PAINT.line;
  for (const s of [-1, 1]) {
    const d = f.wall.pole;
    line(c, m, f.home.x, f.home.z, f.home.x + s * d * k, f.home.z + d * k, 0.1);
    rectLine(c, m, f.home.x + s * 0.45, f.home.z - 0.9, f.home.x + s * 1.65, f.home.z + 0.9, 0.08);
  }
  // Bases and home plate.
  for (const b of [f.first, f.second, f.third]) {
    c.save();
    c.translate(px(m, b.x), py(m, b.z));
    c.rotate(Math.PI / 4);
    c.fillStyle = "#FFFFFF";
    const s = 0.38 * m.ppm;
    c.fillRect(-s / 2, -s / 2, s, s);
    c.restore();
  }
  c.fillStyle = "#FFFFFF";
  c.beginPath();
  const hp = [
    [-0.22, 0.22],
    [0.22, 0.22],
    [0.22, 0],
    [0, -0.22],
    [-0.22, 0],
  ];
  hp.forEach(([x, z], i) => (i === 0 ? c.moveTo(px(m, f.home.x + x!), py(m, f.home.z + z!)) : c.lineTo(px(m, f.home.x + x!), py(m, f.home.z + z!))));
  c.closePath();
  c.fill();
  dot(c, m, f.mound.x, f.mound.z, 0.3, "#FFFFFF");
}

export function paintBasketball(c: Ctx, m: SurfaceMap, f: BasketballField, home: ArenaTeam): void {
  const r = m.rect;
  fillRect(c, m, r.minX, r.minZ, r.maxX, r.maxZ, ARENA_PAINT.courtSurround);
  const hx = f.length / 2;
  const hz = f.width / 2;
  // Maple boards along the court.
  const board = 0.12;
  const rnd = grain(3);
  for (let z = f.cz - hz, i = 0; z < f.cz + hz; z += board, i++) fillRect(c, m, f.cx - hx, z, f.cx + hx, Math.min(f.cz + hz, z + board), i % 2 ? ARENA_PAINT.maple : rnd() < 0.5 ? ARENA_PAINT.mapleAlt : ARENA_PAINT.maple);
  c.strokeStyle = ARENA_PAINT.line;
  const lw = 0.05;
  rectLine(c, m, f.cx - hx, f.cz - hz, f.cx + hx, f.cz + hz, lw);
  line(c, m, f.cx, f.cz - hz, f.cx, f.cz + hz, lw);
  arc(c, m, f.cx, f.cz, f.centreCircle, lw, 0, Math.PI * 2, home.primary);
  arc(c, m, f.cx, f.cz, f.centreCircle, lw);
  text(c, m, "TOWNSY", f.cx, f.cz, 0.85, home.secondary, 0);
  for (const s of [-1, 1]) {
    const rim = f.cx + s * f.hoopX;
    const base = f.cx + s * hx;
    // The key (painted) and the free-throw circle.
    fillRect(c, m, base, f.cz - f.key.width / 2, base - s * f.key.length, f.cz + f.key.width / 2, home.primary);
    c.strokeStyle = ARENA_PAINT.line;
    rectLine(c, m, base, f.cz - f.key.width / 2, base - s * f.key.length, f.cz + f.key.width / 2, lw);
    arc(c, m, base - s * f.key.length, f.cz, 1.8, lw);
    // The three-point line: corner straights, then the arc around the rim.
    const t = f.threePoint;
    const a = Math.asin(Math.min(1, t.corner / t.radius));
    const cornerEnd = rim - s * Math.cos(a) * t.radius;
    for (const side of [-1, 1]) line(c, m, base, f.cz + side * t.corner, cornerEnd, f.cz + side * t.corner, lw);
    // The arc opens towards half court: around angle PI for the east rim, around 0 for the west one.
    if (s > 0) arc(c, m, rim, f.cz, t.radius, lw, Math.PI - a, Math.PI + a);
    else arc(c, m, rim, f.cz, t.radius, lw, -a, a);
    // The restricted-area arc under the rim.
    if (s > 0) arc(c, m, rim, f.cz, 1.22, lw, Math.PI / 2, Math.PI * 1.5);
    else arc(c, m, rim, f.cz, 1.22, lw, -Math.PI / 2, Math.PI / 2);
  }
}

export function paintHockey(c: Ctx, m: SurfaceMap, f: HockeyField, home: ArenaTeam): void {
  const r = m.rect;
  fillRect(c, m, r.minX, r.minZ, r.maxX, r.maxZ, "#3A4252");
  const hx = f.length / 2;
  const hz = f.width / 2;
  const cr = f.cornerRadius;
  // The ice (rounded rectangle) with a faint scrape texture.
  c.fillStyle = ARENA_PAINT.ice;
  c.beginPath();
  const x0 = px(m, f.cx - hx);
  const x1 = px(m, f.cx + hx);
  const y0 = py(m, f.cz + hz);
  const y1 = py(m, f.cz - hz);
  const rr = cr * m.ppm;
  c.moveTo(x0 + rr, y0);
  c.lineTo(x1 - rr, y0);
  c.arcTo(x1, y0, x1, y0 + rr, rr);
  c.lineTo(x1, y1 - rr);
  c.arcTo(x1, y1, x1 - rr, y1, rr);
  c.lineTo(x0 + rr, y1);
  c.arcTo(x0, y1, x0, y1 - rr, rr);
  c.lineTo(x0, y0 + rr);
  c.arcTo(x0, y0, x0 + rr, y0, rr);
  c.closePath();
  c.fill();
  c.save();
  c.clip();
  const rnd = grain(11);
  for (let i = 0; i < 1600; i++) {
    c.strokeStyle = rnd() < 0.5 ? "rgba(160,180,200,0.10)" : "rgba(255,255,255,0.35)";
    c.lineWidth = 1;
    const sx = x0 + rnd() * (x1 - x0);
    const sy = y0 + rnd() * (y1 - y0);
    c.beginPath();
    c.moveTo(sx, sy);
    c.lineTo(sx + (rnd() - 0.5) * 40, sy + (rnd() - 0.5) * 8);
    c.stroke();
  }
  // Centre red line, blue lines, goal lines, the circles and spots.
  c.strokeStyle = ARENA_PAINT.red;
  line(c, m, f.cx, f.cz - hz, f.cx, f.cz + hz, 0.3);
  c.strokeStyle = ARENA_PAINT.blue;
  for (const s of [-1, 1]) line(c, m, f.cx + s * f.blueLineX, f.cz - hz, f.cx + s * f.blueLineX, f.cz + hz, 0.3);
  c.strokeStyle = ARENA_PAINT.red;
  for (const s of [-1, 1]) line(c, m, f.cx + s * f.goalLineX, f.cz - hz, f.cx + s * f.goalLineX, f.cz + hz, 0.05);
  c.strokeStyle = ARENA_PAINT.blue;
  arc(c, m, f.cx, f.cz, f.centreCircle, 0.05);
  dot(c, m, f.cx, f.cz, 0.15, ARENA_PAINT.blue);
  c.strokeStyle = ARENA_PAINT.red;
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      arc(c, m, f.cx + sx * f.faceoff.x, f.cz + sz * f.faceoff.z, f.faceoff.radius, 0.05);
      dot(c, m, f.cx + sx * f.faceoff.x, f.cz + sz * f.faceoff.z, 0.3, ARENA_PAINT.red);
      dot(c, m, f.cx + sx * (f.blueLineX - 1.5), f.cz + sz * f.faceoff.z, 0.3, ARENA_PAINT.red);
    }
    // The goal crease.
    const gx = f.cx + sx * f.goalLineX;
    c.fillStyle = "rgba(70,130,220,0.45)";
    c.beginPath();
    if (sx > 0) c.arc(px(m, gx), py(m, f.cz), 1.83 * m.ppm, Math.PI / 2, Math.PI * 1.5);
    else c.arc(px(m, gx), py(m, f.cz), 1.83 * m.ppm, -Math.PI / 2, Math.PI / 2);
    c.fill();
  }
  // The home team's mark at centre ice.
  arc(c, m, f.cx, f.cz, 3.2, 0.1, 0, Math.PI * 2, home.primary);
  text(c, m, "TOWNSY", f.cx, f.cz, 1.2, home.secondary, 0);
  c.restore();
}

/** Paint a field of any sport. */
export function paintField(c: Ctx, m: SurfaceMap, field: ArenaField, home: ArenaTeam, awayName: string): void {
  switch (field.sport) {
    case "football":
      return paintFootball(c, m, field, home, awayName);
    case "soccer":
      return paintSoccer(c, m, field);
    case "baseball":
      return paintBaseball(c, m, field);
    case "basketball":
      return paintBasketball(c, m, field, home);
    case "hockey":
      return paintHockey(c, m, field, home);
  }
}
